'use client'

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

type LobbyHeaderMetaContextValue = {
  meta: ReactNode | null
  title: ReactNode | null
  setMeta: (meta: ReactNode | null) => void
  setTitle: (title: ReactNode | null) => void
}

const LobbyHeaderMetaContext = createContext<LobbyHeaderMetaContextValue | null>(null)

export function LobbyHeaderMetaProvider({ children }: { children: ReactNode }) {
  const [meta, setMetaState] = useState<ReactNode | null>(null)
  const [title, setTitleState] = useState<ReactNode | null>(null)
  const setMeta = useCallback((next: ReactNode | null) => {
    setMetaState(next)
  }, [])
  const setTitle = useCallback((next: ReactNode | null) => {
    setTitleState(next)
  }, [])
  const value = useMemo(() => ({ meta, title, setMeta, setTitle }), [meta, title, setMeta, setTitle])

  return (
    <LobbyHeaderMetaContext.Provider value={value}>{children}</LobbyHeaderMetaContext.Provider>
  )
}

export function useLobbyHeaderMeta(): ReactNode | null {
  return useContext(LobbyHeaderMetaContext)?.meta ?? null
}

export function useLobbyHeaderTitle(): ReactNode | null {
  return useContext(LobbyHeaderMetaContext)?.title ?? null
}

/** Register page metrics in the desktop lobby shell header (next to the title). */
export function LobbyHeaderMeta({ children }: { children: ReactNode }) {
  const ctx = useContext(LobbyHeaderMetaContext)

  useLayoutEffect(() => {
    if (!ctx) return undefined
    ctx.setMeta(children)
    return () => ctx.setMeta(null)
  }, [children, ctx])

  return null
}

/** Replace the default lobby header title (e.g. breadcrumb on nested routes). */
export function LobbyHeaderTitle({ children }: { children: ReactNode }) {
  const ctx = useContext(LobbyHeaderMetaContext)

  useLayoutEffect(() => {
    if (!ctx) return undefined
    ctx.setTitle(children)
    return () => ctx.setTitle(null)
  }, [children, ctx])

  return null
}
